import React, { useState, useEffect } from 'react';
import { Modal } from '@common/Modal';
import { Button } from '@common/Button';
import { AlertTriangle, Info, MapPin, Map } from 'lucide-react';
import { getStructureDetails } from '../../../../utils/wfs/importHauteursLibres';
import { logError } from '../../../../utils/logger';

export const HauteursLibresModal = ({ isOpen, onClose, structure }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && structure?.reference) {
      loadDetails();
    }
  }, [isOpen, structure]);

  const loadDetails = async () => {
    if (!structure?.reference) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const structureDetails = await getStructureDetails(structure.reference);
      // If the structure already has details and routes, use them directly
      if (structureDetails) {
        setDetails(structureDetails);
      } else {
        // If no details were returned, use the structure itself
        setDetails(structure);
      }
    } catch (error) {
      logError(`Failed to load details for structure ${structure.reference}:`, error);
      setError('Impossible de charger les détails de la structure');
    } finally {
      setLoading(false);
    }
  };

  // Find the minimum height across all measurements
  const getMinimumHeight = () => {
    if (!details?.details || !Array.isArray(details.details) || details.details.length === 0) {
      return details?.hauteur || structure?.hauteur || 'N/A';
    }
    
    const heights = details.details
      .flatMap(detail => [
        detail.gauche,
        detail.centre_gauche,
        detail.centre,
        detail.centre_droit,
        detail.droite
      ])
      .filter(height => height !== null && height !== undefined);
    
    if (heights.length === 0) return structure?.hauteur || 'N/A';
    
    return Math.min(...heights).toFixed(2);
  };

  // Get the most recent measurement date
  const getLatestMeasurementDate = () => {
    if (!details?.details || !Array.isArray(details.details) || details.details.length === 0) {
      return details?.updatedAt ? new Date(details.updatedAt).toLocaleDateString() : 'N/A';
    }
    
    const dates = details.details
      .map(detail => detail.date)
      .filter(Boolean)
      .map(date => new Date(date));
    
    if (dates.length === 0) return 'N/A';
    
    const latestDate = new Date(Math.max(...dates));
    return latestDate.toLocaleDateString();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Structure Details">
      <div className="p-6 space-y-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading details...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Error</p>
              <p>{error}</p>
            </div>
          </div>
        ) : details ? (
          <>
            {/* Structure Info */}
            <div className="bg-blue-500/10 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-white mb-2">{details.obstacle}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400 mb-1">Municipality</p>
                  <p className="text-white">{details.municipalite}</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-1">Reference</p>
                  <p className="text-white">{details.reference}</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-1">Minimum Height</p>
                  <p className="text-white font-medium">{getMinimumHeight()} m</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-1">Last Measurement</p>
                  <p className="text-white">{getLatestMeasurementDate()}</p>
                </div>
                <div className="col-span-2 bg-blue-500/10 p-3 rounded-lg">
                  <p className="text-gray-300 mb-2 font-medium">Coordinates</p>
                  {details.coordinates || (details.latitude && details.longitude) ? (
                    <>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-blue-400" />
                        <p className="text-white">
                          {details.coordinates ? 
                            `${details.coordinates.latitude.toFixed(6)}, ${details.coordinates.longitude.toFixed(6)}` : 
                            `${details.latitude.toFixed(6)}, ${details.longitude.toFixed(6)}`}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Map className="w-5 h-5 text-gray-400" />
                      <p className="text-gray-400">No coordinates available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Routes */}
            <div>
              <h3 className="text-white font-medium mb-2">Routes</h3>
              <div className="bg-gray-700/50 rounded-lg overflow-x-auto max-h-60 overflow-y-auto">
                {details.routes && Array.isArray(details.routes) && details.routes.length > 0 ? (
                  <table className="w-full text-sm table-fixed">
                    <thead className="bg-gray-800/50">
                      <tr>
                        <th className="text-left py-2 px-4 text-gray-300 w-1/3">Route</th>
                        <th className="text-left py-2 px-4 text-gray-300 w-1/3">Sequence</th>
                        <th className="text-left py-2 px-4 text-gray-300 w-1/3">Municipality</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.routes.map((route, index) => (
                        <tr key={index} className="border-t border-gray-700">
                          <td className="py-2 px-4 text-white truncate">{route.route}</td>
                          <td className="py-2 px-4 text-gray-300 truncate">{route.sequence}</td>
                          <td className="py-2 px-4 text-gray-300 truncate">{route.municipalite}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-4 text-center text-gray-400">
                    <p>No route information available</p>
                    {details.route && (
                      <p className="mt-2">Main route: {details.route} {details.sequence ? `(Sequence: ${details.sequence})` : ''}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Detailed Measurements */}
            <div>
              <h3 className="text-white font-medium mb-2">Detailed Measurements</h3>
              <div className="bg-gray-700/50 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
                {details.details && Array.isArray(details.details) && details.details.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800/50">
                      <tr>
                        <th className="text-left py-2 px-3 text-gray-300 w-12">Point</th>
                        <th className="text-left py-2 px-3 text-gray-300 w-24">Direction</th>
                        <th className="text-left py-2 px-3 text-gray-300 w-16">Left</th>
                        <th className="text-left py-2 px-3 text-gray-300 w-16">Center L</th>
                        <th className="text-left py-2 px-3 text-gray-300 w-16">Center</th>
                        <th className="text-left py-2 px-3 text-gray-300 w-16">Center R</th>
                        <th className="text-left py-2 px-3 text-gray-300 w-16">Right</th>
                        <th className="text-left py-2 px-3 text-gray-300 w-40">Notes</th>
                        <th className="text-left py-2 px-3 text-gray-300 w-24">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.details.map((detail, index) => (
                        <tr key={index} className="border-t border-gray-700">
                          <td className="py-2 px-3 text-white whitespace-nowrap">{detail.point}</td>
                          <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{detail.direction}</td>
                          <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{detail.gauche}</td>
                          <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{detail.centre_gauche}</td>
                          <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{detail.centre}</td>
                          <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{detail.centre_droit}</td>
                          <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{detail.droite}</td>
                          <td className="py-2 px-3 text-gray-300 truncate max-w-xs">{detail.remarque}</td>
                          <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{detail.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-4 text-center text-gray-400">
                    <p>No detailed measurements available</p>
                    {details.hauteur && (
                      <p className="mt-2">Main height: {details.hauteur} m</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Information Note */}
            <div className="bg-gray-700/30 p-4 rounded-lg flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <div className="text-sm text-gray-300">
                <p className="mb-1">
                  Measurements are expressed in meters and may vary depending on weather conditions and road work.
                </p>
                <p>
                  It is recommended to allow a safety margin of at least 10 cm when planning routes.
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-400">
            No information available for this structure
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={onClose}
            className="bg-gray-700 hover:bg-gray-600 text-white"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HauteursLibresModal;